import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import ReactLoading from "react-loading";

const EditTournamentModal = ({
  isOpen,
  tournament,
  onClose,
  onUpdate,
  isSubmitting = false,
}) => {
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [imageLoading, setImageLoading] = useState(false);

  useEffect(() => {
    if (tournament?.imageUrl) {
      setImageLoading(true);
      setImagePreview(tournament.imageUrl);
    } else {
      setImagePreview(null);
    }
    setSelectedFile(null);
  }, [tournament]);

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        toast.error("Please select a valid image file");
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should be less than 5MB");
        return;
      }

      setSelectedFile(file);
      setImageLoading(true);

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setImageLoading(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpdate = () => {
    if (!selectedFile) {
      toast.info("Please select an image to update");
      return;
    }
    onUpdate(selectedFile);
  };

  const handleClose = () => {
    setImagePreview(tournament?.imageUrl || null);
    setSelectedFile(null);
    onClose();
  };

  if (!isOpen || !tournament) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-auto bg-gray-900 bg-opacity-50">
      <div className="w-full max-w-2xl rounded-lg bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-800">
            Edit Tournament
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700"
            disabled={isSubmitting}
          >
            <svg
              className="h-6 w-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="space-y-4">
          {/* Tournament Name - Read Only */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Tournament Name
            </label>
            <input
              type="text"
              value={tournament.tournamentName || ""}
              readOnly
              disabled
              className="mt-1 w-full rounded-md border border-gray-300 bg-gray-200 px-3 py-2 text-gray-700 cursor-not-allowed opacity-75"
            />
          </div>

          {/* Owner Name - Read Only */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Owner Name
            </label>
            <input
              type="text"
              value={tournament.ownerName || ""}
              readOnly
              disabled
              className="mt-1 w-full rounded-md border border-gray-300 bg-gray-200 px-3 py-2 text-gray-700 cursor-not-allowed opacity-75"
            />
          </div>

          {/* Tournament Type - Read Only */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Tournament Type
            </label>
            <input
              type="text"
              value={tournament.tournamentType || ""}
              readOnly
              disabled
              className="mt-1 w-full rounded-md border border-gray-300 bg-gray-200 px-3 py-2 text-gray-700 cursor-not-allowed opacity-75"
            />
          </div>

          {/* Sport - Read Only */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Sport
            </label>
            <input
              type="text"
              value={tournament.sport || ""}
              readOnly
              disabled
              className="mt-1 w-full rounded-md border border-gray-300 bg-gray-200 px-3 py-2 text-gray-700 cursor-not-allowed opacity-75"
            />
          </div>

          {/* Image Upload - Editable */}
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Image
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="file:py-2 file:px-4 file:bg-blue-100 file:border file:border-blue-300 file:rounded file:text-blue-700 file:font-semibold mt-1 w-full"
            />
            {tournament?.imageUrl && !selectedFile && (
              <div className="mt-2 text-sm text-gray-600">
                Current image will be kept if no new image is selected
              </div>
            )}
            {imagePreview && (
              <div className="mt-4 relative">
                {imageLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-lg">
                    <ReactLoading
                      type={"spin"}
                      color={"#4318FF"}
                      height={40}
                      width={40}
                    />
                  </div>
                )}
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="h-32 w-32 object-cover rounded-lg"
                  onLoad={() => setImageLoading(false)}
                  onError={() => setImageLoading(false)}
                />
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="cursor-pointer rounded-md bg-gray-400 px-5 py-2 text-white hover:bg-gray-500 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleUpdate}
            disabled={isSubmitting}
            className="bg-theme cursor-pointer rounded-md px-5 py-2 text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {isSubmitting ? "Updating..." : "Update Image"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditTournamentModal;